import { TreeDataItem, TreeView } from "@/components/tree-view";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MessageCard, MessageCardSkeleton } from "@cohesive/ui-components";
import { debounce } from "lodash";
import {
  AtSign,
  Hash as ChannelIcon,
  LayoutGrid,
  User,
  Users,
} from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import { EntityDragRow } from "../../dnd/EntityDragRow";
import { buildGenericById, toGenericArray } from "../../genericProjection";
import { BaseSelectorProps } from "../BaseSelector";
import {
  Selector,
  SelectorEmpty,
  SelectorLoading,
  SelectorResults,
  SelectorSearch,
  SelectorSidebar,
} from "../Selector";
import { useSlackMessagesSearchInfinite } from "./useSlackMessagesSearch";
import { useSlackUserConversationsList } from "./useSlackUserConversationsList";

// Tree item type for TreeView
interface TreeDatItemCustom extends TreeDataItem {
  raw?: any;
}

export const SlackSelector: React.FC<BaseSelectorProps> = ({
  connection,
  onSelect,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedTreeItem, setSelectedTreeItem] =
    useState<TreeDatItemCustom | null>(null);

  // Debounce search input
  const debouncedSetSearch = useMemo(
    () => debounce((query: string) => setDebouncedQuery(query), 500),
    [],
  );
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    debouncedSetSearch(value);
  };

  // Fetch all channels (includes public, private, IMs, MPIMs). genericById
  // carries the channels' generic projections (entity_format=both) so the
  // sidebar places generic channels while the tree renders the typed ones.
  const {
    data: channelsData,
    isLoading: isChannelsLoading,
    genericById: channelGenericById,
  } = useSlackUserConversationsList({
    connection: connection,
    limit: 1000,
    types: "public_channel,private_channel,im,mpim",
    exclude_archived: true,
  });

  // Build tree data for TreeView
  const treeData: TreeDatItemCustom[] = useMemo(() => {
    if (!channelsData) return [];
    const shared: TreeDatItemCustom[] = [];
    const channels: TreeDatItemCustom[] = [];
    const dms: TreeDatItemCustom[] = [];
    const extShared: TreeDatItemCustom[] = [];
    const apps: TreeDatItemCustom[] = [];
    for (const entity of channelsData) {
      const c = entity.data;
      if (c.is_shared) {
        const name =
          c.name_normalized ||
          c.name ||
          c.im_user_name ||
          "Unnamed Shared Channel";
        shared.push({
          id: c.id,
          name: name,
          icon: ChannelIcon,
          raw: c,
          actions: [
            <Button
              aria-label="Mention in chat"
              variant="ghost"
              size="icon"
              className="!p-2 rounded-full w-5 h-5"
              onClick={(e) => {
                e.stopPropagation();
                // Place the channel's generic projection (entity_format=both) so
                // only generics land on the canvas; fall back to the typed
                // channel if its projection is missing.
                return onSelect(
                  channelGenericById.get(entity.data.id) ?? entity,
                );
              }}
            >
              <AtSign className="!size-3.5" />
            </Button>,
          ],
        });
      } else if (c.is_channel && !c.is_mpim) {
        const name =
          c.name_normalized || c.name || c.im_user_name || "Unnamed Channel";
        channels.push({
          id: c.id,
          name: name,
          icon: ChannelIcon,
          raw: c,
          actions: [
            <Button
              aria-label="Mention in chat"
              variant="ghost"
              size="icon"
              className="!p-2 rounded-full w-5 h-5"
              onClick={(e) => {
                e.stopPropagation();
                // Place the channel's generic projection (entity_format=both) so
                // only generics land on the canvas; fall back to the typed
                // channel if its projection is missing.
                return onSelect(
                  channelGenericById.get(entity.data.id) ?? entity,
                );
              }}
            >
              <AtSign className="!size-3.5" />
            </Button>,
          ],
        });
      } else if (c.is_ext_shared) {
        const name =
          c.name_normalized || c.name || c.im_user_name || "Unnamed Channel";
        extShared.push({
          id: c.id,
          name: name,
          icon: ChannelIcon,
          raw: c,
          actions: [
            <Button
              aria-label="Mention in chat"
              variant="ghost"
              size="icon"
              className="!p-2 rounded-full w-5 h-5"
              onClick={(e) => {
                e.stopPropagation();
                // Place the channel's generic projection (entity_format=both) so
                // only generics land on the canvas; fall back to the typed
                // channel if its projection is missing.
                return onSelect(
                  channelGenericById.get(entity.data.id) ?? entity,
                );
              }}
            >
              <AtSign className="!size-3.5" />
            </Button>,
          ],
        });
      } else if (c.is_bot) {
        const name =
          c.name_normalized || c.name || c.im_user_name || "Unnamed Bot";
        apps.push({
          id: c.id,
          name: name,
          icon: c.im_user_image_url ? (
            <img src={c.im_user_image_url} className="w-4 h-4 rounded-full" />
          ) : (
            LayoutGrid
          ),
          raw: c,
          actions: [
            <Button
              aria-label="Mention in chat"
              variant="ghost"
              size="icon"
              className="!p-2 rounded-full w-5 h-5"
              onClick={(e) => {
                e.stopPropagation();
                // Place the channel's generic projection (entity_format=both) so
                // only generics land on the canvas; fall back to the typed
                // channel if its projection is missing.
                return onSelect(
                  channelGenericById.get(entity.data.id) ?? entity,
                );
              }}
            >
              <AtSign className="!size-3.5" />
            </Button>,
          ],
        });
      } else if (c.is_im || c.is_mpim) {
        const name =
          c.im_user_name || c.name_normalized || c.name || "Direct Message";
        dms.push({
          id: c.id,
          name: name,
          icon: c.im_user_image_url ? (
            <img src={c.im_user_image_url} className="w-4 h-4 rounded-full" />
          ) : c.is_mpim ? (
            Users
          ) : (
            User
          ),
          raw: c,
          actions: [
            <Button
              aria-label="Mention in chat"
              variant="ghost"
              size="icon"
              className="!p-2 rounded-full w-5 h-5"
              onClick={(e) => {
                e.stopPropagation();
                // Place the channel's generic projection (entity_format=both) so
                // only generics land on the canvas; fall back to the typed
                // channel if its projection is missing.
                return onSelect(
                  channelGenericById.get(entity.data.id) ?? entity,
                );
              }}
            >
              <AtSign className="!size-3.5" />
            </Button>,
          ],
        });
      }
    }
    return [
      shared.length > 0 && {
        id: "shared-root",
        name: "External Connections",
        icon: ChannelIcon,
        children: shared.sort((a, b) => a.name.localeCompare(b.name)),
      },
      extShared.length > 0 && {
        id: "ext-shared-root",
        name: "External Connections",
        icon: ChannelIcon,
        children: extShared.sort((a, b) => a.name.localeCompare(b.name)),
      },
      channels.length > 0 && {
        id: "channels-root",
        name: "Channels",
        icon: ChannelIcon,
        children: channels.sort((a, b) => a.name.localeCompare(b.name)),
      },
      dms.length > 0 && {
        id: "dms-root",
        name: "Direct Messages",
        icon: User,
        children: dms.sort((a, b) => a.name.localeCompare(b.name)),
      },
      apps.length > 0 && {
        id: "bots-root",
        name: "Apps",
        icon: LayoutGrid,
        children: apps.sort((a, b) => a.name.localeCompare(b.name)),
      },
    ].filter(Boolean) as TreeDatItemCustom[];
  }, [channelsData, channelGenericById, onSelect]);

  // Determine selected channel/DM
  const selectedChannelOrDM = selectedTreeItem?.raw;

  // If a search query is present, use search.messages with in: filter
  const searchInFilter = useMemo(() => {
    if (!selectedChannelOrDM) return "";

    if (selectedChannelOrDM.is_im) {
      return `in:<@${selectedChannelOrDM.im_user_id}>`;
    }

    return `in:${selectedChannelOrDM.name}`;
  }, [selectedChannelOrDM]);

  // Search messages in selected channel/DM with infinite scrolling
  const {
    data,
    isLoading: isSearchLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useSlackMessagesSearchInfinite({
    connection: connection,
    query: [debouncedQuery, searchInFilter, "-is:thread"]
      .filter(Boolean)
      .join(" ")
      .trim(),
    sort: debouncedQuery ? "score" : "timestamp",
    limit: 20,
  });

  // Flatten all typed messages from all pages (each page is the full toolbox
  // response: typed messages in `data`, generic projections in `data_generic`).
  const searchData =
    (data as any)?.pages?.flatMap((page: any) => page.data) || [];

  // Generic projections (entity_format=both) keyed by message id — placed on the
  // canvas in place of the typed message, which only drives the rich card UI.
  const genericById = useMemo(
    () =>
      buildGenericById(
        (data as any)?.pages?.flatMap((page: any) =>
          toGenericArray(page.data_generic),
        ) ?? [],
      ),
    [data],
  );

  // Handle scroll to load more
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (
        scrollHeight - scrollTop <= clientHeight * 1.2 &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  // Render right pane: threads or search results
  const searchDataIds = useMemo(
    () => searchData.map((e: any) => e.data.id),
    [searchData],
  );

  const renderThreadsOrSearch = () => {
    if (isSearchLoading) return <SelectorLoading />;
    if (!searchData.length) {
      if (debouncedQuery) {
        return <SelectorEmpty message="No search results found" />;
      }

      if (!selectedChannelOrDM) {
        return (
          <SelectorEmpty message="Select a channel or DM to view threads" />
        );
      } else {
        return (
          <SelectorEmpty message="No threads found in this conversation" />
        );
      }
    }
    return (
      <div className="divide-y w-full">
        {searchData.map((entity: any, index: number) => {
          return (
            <EntityDragRow
              key={`${entity.data.id}-${index}`}
              entity={entity}
              generic={genericById.get(entity.data.id)}
              entityId={entity.data.id}
              orderedIds={searchDataIds}
              onSelect={onSelect}
              dragScope="slack"
            >
              <MessageCard
                {...entity.data}
                platform={entity.platform}
                className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
              />
            </EntityDragRow>
          );
        })}
        {/* Loading indicator for next page */}
        {isFetchingNextPage && (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <MessageCardSkeleton
                key={`loading-${i}`}
                className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Selector>
      <SelectorSidebar>
        {isChannelsLoading ? (
          <div className="p-2 space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        ) : (
          <TreeView
            data={treeData}
            onSelectChange={(item) => setSelectedTreeItem(item || null)}
            expandAll={false}
          />
        )}
      </SelectorSidebar>
      <SelectorSearch
        value={searchQuery}
        onChange={handleSearchChange}
        placeholder={
          selectedChannelOrDM
            ? `Search in ${selectedChannelOrDM.name || selectedChannelOrDM.im_user_name || "conversation"}...`
            : "Select a channel or DM to search..."
        }
        disabled={!selectedChannelOrDM}
      />
      <SelectorResults
        onScroll={handleScroll}
        style={{ paddingBottom: "200px" }}
      >
        {renderThreadsOrSearch()}
      </SelectorResults>
    </Selector>
  );
};
